import * as React from 'react'
import { useTranslation } from 'react-i18next'

import {
  EMAIL_DIRECTION,
  isEmailDelivered,
  isEmailOpened,
} from '#/entities/shipment'
import type { ShipmentEmailFeedItem } from '#/entities/shipment'
import {
  EmailIcon,
  MessageTickDoubleIcon,
  MessageTickSingleIcon,
} from '#/shared/ui/icons'
import { HistoryAppTag, NoteRow } from '#/shared/ui/history-row'
import { RichText } from '#/shared/ui/rich-text'

import { EmailEventsHoverCard } from './email-events-hover-card'

function emailMeta(item: ShipmentEmailFeedItem): React.ReactNode[] {
  const meta: React.ReactNode[] = []
  if (item.timestamp) meta.push(item.timestamp)
  if (item.isApp) meta.push(<HistoryAppTag />)
  if (item.direction === EMAIL_DIRECTION.inbound) {
    if (item.sender) meta.push(item.sender)
  } else if (item.recipient) {
    meta.push(item.recipient)
  }
  return meta
}

/** WhatsApp-style receipt: ✓ sent, ✓✓ delivered, green ✓✓ opened. */
function DeliveryTick({ item }: { item: ShipmentEmailFeedItem }) {
  if (isEmailOpened(item))
    return <MessageTickDoubleIcon className="size-4 text-green-600" />
  if (isEmailDelivered(item))
    return <MessageTickDoubleIcon className="size-4 text-gray-500" />
  return <MessageTickSingleIcon className="size-4 text-gray-500" />
}

function EmailRowAction({ item }: { item: ShipmentEmailFeedItem }) {
  if (item.direction !== EMAIL_DIRECTION.outbound) return null
  return (
    <EmailEventsHoverCard emailId={item.id}>
      <DeliveryTick item={item} />
    </EmailEventsHoverCard>
  )
}

function EmailRowTitle({ subject, body }: { subject: string; body: string }) {
  return (
    <div className="flex flex-col gap-1.25">
      <span className="text-sm text-gray-900">{subject}</span>
      {body && (
        <RichText
          html={body}
          className="text-xs leading-relaxed text-gray-600"
        />
      )}
    </div>
  )
}

export function EmailHistoryRow({ email }: { email: ShipmentEmailFeedItem }) {
  const { t } = useTranslation()
  const subject = email.subject || t('orderDetails.history.email.noSubject')
  return (
    <NoteRow
      icon={EmailIcon}
      title={<EmailRowTitle subject={subject} body={email.body} />}
      meta={emailMeta(email)}
      action={<EmailRowAction item={email} />}
      expandable={Boolean(email.body)}
    />
  )
}
